import type { IDays, IProductionCapacitySlot, ISchedule, IShopProductionCapacity } from '@app/common';

export type WeekCapacity = IShopProductionCapacity['capacity'];

export const WEEK_DAYS: IDays[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const SLOT_DURATION = 15;

// Clé de la limite affichée : 'orders' pour maxOrders, sinon l'id du tag pour maxProducts[tagId]
export const ORDERS_METRIC = 'orders';

export type SlotKey = string;

export interface CapacitySection {
  key: string;
  name: string;
  range: string;
  starts: number[];
}

export type OpenStarts = Record<IDays, Set<number>>;

export type CopyMode = 'replace' | 'fill';

export interface CapacityMetric {
  key: string;
  label: string;
  unit: string;
}

export const formatHHMM = (minutes: number) => {
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;

  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

export const slotKey = (day: IDays, start: number): SlotKey => `${day}:${start}`;

export const parseSlotKey = (key: SlotKey): { day: IDays; start: number } => {
  const [day, start] = key.split(':');

  return { day: day as IDays, start: Number(start) };
};

export const getOpenRanges = (schedules: (ISchedule | undefined)[], day: IDays): number[][] => {
  return schedules
    .flatMap((schedule) => schedule?.[day] || [])
    .filter((range) => range.length >= 2);
};

// Créneaux réellement ouverts : présents dans la capacité du formulaire et inclus dans une plage d'ouverture
// (la capacité contient aussi les créneaux de la coupure entre midi et soir, qu'on n'affiche pas)
export const buildOpenStarts = (capacity: Partial<WeekCapacity> | undefined, schedules: (ISchedule | undefined)[]): OpenStarts => {
  return Object.fromEntries(
    WEEK_DAYS.map((day) => {
      const ranges = getOpenRanges(schedules, day);

      const starts = (capacity?.[day] || [])
        .map((slot) => slot.start)
        .filter((start) => ranges.some(([from, to]) => start >= from && start + SLOT_DURATION <= to));

      return [day, new Set(starts)];
    }),
  ) as OpenStarts;
};

// Services affichés dans la grille, par heure de début : une plage continue qui déborde est découpée
const SERVICES = [
  { name: 'Matin', from: 0 },
  { name: 'Midi', from: 11 * 60 },
  { name: 'Soir', from: 18 * 60 },
];

const getServiceIndex = (start: number) => SERVICES.reduce((found, service, index) => (start >= service.from ? index : found), 0);

const groupConsecutive = (starts: Iterable<number>): number[][] => {
  const groups: number[][] = [];

  [...new Set(starts)].sort((a, b) => a - b).forEach((start) => {
    const current = groups[groups.length - 1];

    if (current && start - current[current.length - 1] === SLOT_DURATION) {
      current.push(start);
    } else {
      groups.push([start]);
    }
  });

  return groups;
};

export const formatOpenHours = (starts: Set<number>) => {
  return groupConsecutive(starts)
    .map((group) => `${formatHHMM(group[0])}–${formatHHMM(group[group.length - 1] + SLOT_DURATION)}`)
    .join(' · ');
};

export const buildSections = (openStarts: OpenStarts): CapacitySection[] => {
  const groups = groupConsecutive(WEEK_DAYS.flatMap((day) => [...openStarts[day]])).flatMap((group) => {
    const byService: number[][] = [];

    group.forEach((start) => {
      const current = byService[byService.length - 1];

      if (current && getServiceIndex(current[0]) === getServiceIndex(start)) {
        current.push(start);
      } else {
        byService.push([start]);
      }
    });

    return byService;
  });

  return groups.map((group) => ({
    key: String(group[0]),
    name: SERVICES[getServiceIndex(group[0])].name,
    range: `${formatHHMM(group[0])} – ${formatHHMM(group[group.length - 1] + SLOT_DURATION)}`,
    starts: group,
  }));
};

const isLimit = (value: unknown): value is number => typeof value === 'number' && !isNaN(value);

export const getSlotLimit = (slot: IProductionCapacitySlot | undefined, metric: string): number | undefined => {
  const value = metric === ORDERS_METRIC ? slot?.maxOrders : slot?.maxProducts?.[metric];

  return isLimit(value) ? value : undefined;
};

// undefined = pas de limite
export const setSlotLimit = (slot: IProductionCapacitySlot, metric: string, value: number | undefined): IProductionCapacitySlot => {
  if (metric === ORDERS_METRIC) {
    return { ...slot, maxOrders: value };
  }

  const { [metric]: _, ...maxProducts } = slot.maxProducts || {};

  return { ...slot, maxProducts: isLimit(value) ? { ...maxProducts, [metric]: value } : maxProducts };
};

const updateSlots = (
  capacity: WeekCapacity,
  update: (day: IDays, slot: IProductionCapacitySlot) => IProductionCapacitySlot,
): WeekCapacity => {
  return Object.fromEntries(
    WEEK_DAYS.map((day) => [day, (capacity[day] || []).map((slot) => update(day, slot))]),
  ) as WeekCapacity;
};

export const applyLimit = (capacity: WeekCapacity, keys: SlotKey[], metric: string, value: number | undefined): WeekCapacity => {
  const targets = new Set(keys);

  return updateSlots(capacity, (day, slot) => (
    targets.has(slotKey(day, slot.start)) ? setSlotLimit(slot, metric, value) : slot
  ));
};

export const clearDayLimits = (capacity: WeekCapacity, day: IDays, metric: string): WeekCapacity => {
  return updateSlots(capacity, (slotDay, slot) => (slotDay === day ? setSlotLimit(slot, metric, undefined) : slot));
};

export const removeMetricLimits = (capacity: WeekCapacity, metric: string): WeekCapacity => {
  return updateSlots(capacity, (_, slot) => setSlotLimit(slot, metric, undefined));
};

// Recopie créneau par créneau, uniquement sur les créneaux ouverts à la fois le jour source et le jour cible
export const copyDayLimits = (
  capacity: WeekCapacity,
  { from, to, metrics, mode, openStarts }: { from: IDays; to: IDays[]; metrics: string[]; mode: CopyMode; openStarts: OpenStarts },
): WeekCapacity => {
  const sourceSlots = new Map((capacity[from] || []).map((slot) => [slot.start, slot]));
  const targets = new Set(to);

  return updateSlots(capacity, (day, slot) => {
    if (!targets.has(day) || !openStarts[from].has(slot.start) || !openStarts[day].has(slot.start)) {
      return slot;
    }

    const source = sourceSlots.get(slot.start);

    return metrics.reduce((current, metric) => {
      if (mode === 'fill' && getSlotLimit(current, metric) !== undefined) {
        return current;
      }

      return setSlotLimit(current, metric, getSlotLimit(source, metric));
    }, slot);
  });
};

export const getCopyMismatch = (openStarts: OpenStarts, from: IDays, to: IDays, sections: CapacitySection[]) => {
  const ignored: { name: string; count: number }[] = [];
  let unchanged = 0;

  sections.forEach((section) => {
    let count = 0;

    section.starts.forEach((start) => {
      const inSource = openStarts[from].has(start);
      const inTarget = openStarts[to].has(start);

      if (inSource && !inTarget) count++;
      if (!inSource && inTarget) unchanged++;
    });

    if (count) {
      const existing = ignored.find((entry) => entry.name === section.name);

      if (existing) existing.count += count;
      else ignored.push({ name: section.name, count });
    }
  });

  return { ignored, unchanged };
};
